import { useCallback, useEffect, useMemo, useState } from "react";
import api from "api/api";

// ag grid
import { AgGridReact } from "ag-grid-react";

// mui components & icons
import Tooltip from "@mui/material/Tooltip";

// redux
import { showNotification } from "store/features/notificationSlice";
import { useDispatch, useSelector } from "react-redux";

// components
import AutoDeckReportShare from "./AutoDeckReportShare";
import Pagination from "../common/Pagination";
import Loader from "../common/Loader";

// utils
import GetIconHeader from "utils/GetIconHeader";

// assets
import products from "../../assets/icons/productCategory.png";
import studyPeriods from "../../assets/icons/study_period.png";
import identifier from "../../assets/icons/identifier.png";
import downloadIcon from "../../assets/icons/download.png";
import studyReference from "../../assets/icons/studyReference.png";
import action from "../../assets/icons/studyReference.png";
import studyType from "../../assets/icons/studyType.png";
import competitorProduct from "../../assets/icons/competitorProduct.png";
import share from "../../assets/icons/share.png";
import deleteIcon from "../../assets/icons/deleteIcon.png";
import sapmleInformationIcon from "../../assets/icons/auto-deck-sample-info-icon.png";
import createdByIcon from "../../assets/icons/created-by-icon.png";

function ReportGenerationView() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [rowData, setRowData] = useState([]);

    // redux states
    const role = useSelector((state) => state.userStore.role) || null;
    const userId = useSelector((state) => state.userStore.id);

    // share report
    const [openShareDialog, setOpenShareDialog] = useState(false);
    const [selectedReportForShare, setSelectedReportForShare] = useState({});

    // pagination
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [nextUrl, setNextUrl] = useState(null);
    const [previousUrl, setPreviousUrl] = useState(null);

    const handlePageSizeChange = (event, newPageSize) => {
        setPageSize(newPageSize);
    };

    const handleNextPageClick = () => {
        setPage(page + 1);
    };
    const handlePreviousPageClick = () => {
        setPage(page - 1);
    };

    const handleClickOpenShareDialog = (data = {}) => {
        setSelectedReportForShare(data);
        setOpenShareDialog(true);
    };

    const handleCloseShareDialog = () => {
        setOpenShareDialog(false);
        setSelectedReportForShare({});
    };

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const getReports = useCallback(async () => {
        setIsLoading(true);
        try {
            let url = role && role.toLowerCase() === "admin" ? process.env.REACT_APP_SERVER_PATH + `/auto-deck-reports/all/?archive=False` : process.env.REACT_APP_SERVER_PATH + `/auto-deck-reports/shared/?archive=False`;
            if (pageSize) {
                url += `&limit=${pageSize}`;
            }
            if (page) {
                url += `&offset=${page * pageSize}`;
            }
            await api
                .get(url)
                .then(async (res) => {
                    const results = res?.data?.results || [];
                    setRowData(results);
                    setTotalCount(results.length || 0);
                    setNextUrl(res?.data.next || null);
                    setPreviousUrl(res?.data.previous || null);
                    setIsLoading(false);
                })
                .catch(async (error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    }, [handleError, page, pageSize]);

    useEffect(() => {
        getReports();
    }, [getReports]);

    const handleDeleteReport = async (params) => {
        try {
            const url = `${process.env.REACT_APP_SERVER_PATH}/auto-deck-reports/${params.id}/`;
            await api
                .delete(url)
                .then(() => {
                    handleError("success", "Report deleted");
                    getReports();
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const handleDownloadReport = (params) => {
        if (window.location.href.split("/").indexOf("autodeck-report")) {
            const url = `${process.env.REACT_APP_SERVER_PATH}/auto-deck-reports/download/${params.data.id}/`;
            window.open(url);
        }
    };

    const multipleCellRenderer = (params) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
            return (
                <div className="ag-multi-line-cell-render">
                    {params.value.map((val, i) => {
                        return <p key={i}>{val}</p>;
                    })}
                </div>
            );
        } else {
            return params.value;
        }
    };

    const productsCellRenderer = (params) => {
        const values = Array.isArray(params.value) ? params.value : [params.value];

        return (
            <div className="ag-multi-line-cell-render">
                {values.map((val, idx) => {
                    const proTitle = `${val}__${params.data.core_data_ids}`;

                    return (
                        <p
                            key={`${proTitle}_${idx}`}
                            style={{ display: "flex" }}
                        >
                            <span>{val}</span>
                        </p>
                    );
                })}
            </div>
        );
    };

    const columnDefs = [
        {
            headerName: "Identifier",
            field: "identifier",
            headerComponentParams: {
                template: GetIconHeader("Identifier", identifier),
            },
        },
        {
            headerName: "Study Type",
            field: "study_type",
            headerComponentParams: {
                template: GetIconHeader("Study Type", competitorProduct),
            },
        },
        {
            headerName: "Study Type Method",
            field: "study_type_method",
            headerComponentParams: {
                template: GetIconHeader("Study Type Method", studyType),
            },
        },
        {
            headerName: "Study Period",
            field: "study_period",
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            wrapText: true,
            headerComponentParams: {
                template: GetIconHeader("Study Period", studyPeriods),
            },
        },
        {
            headerName: "Study Reference",
            field: "study_reference",
            minWidth: 350,
            autoHeight: true,
            wrapText: true,
            cellRenderer: multipleCellRenderer,
            headerComponentParams: {
                template: GetIconHeader("Study Reference", studyReference),
            },
        },
        {
            headerName: "Products",
            field: "user_selected_info.product_codes",
            minWidth: 450,
            autoHeight: true,
            wrapText: true,
            cellRenderer: productsCellRenderer,
            headerComponentParams: {
                template: GetIconHeader("Products", products),
            },
        },
        {
            headerName: "Sample Information",
            field: "sample_information",
            autoHeight: true,
            wrapText: true,
            cellRenderer: multipleCellRenderer,
            headerComponentParams: {
                template: GetIconHeader("Sample Information", sapmleInformationIcon),
            },
        },
        {
            headerName: "Created By",
            field: "created_by__email",
            headerComponentParams: {
                template: GetIconHeader("Created By", createdByIcon),
            },
        },
        {
            headerName: "Action",
            field: "action",
            hide: role && role.toLowerCase() !== "admin",
            maxWidth: 150,
            cellRenderer: (params) => (
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Tooltip
                        title={"Share"}
                        placement="top"
                    >
                        <img
                            src={share}
                            alt="Share"
                            style={{
                                width: "22px",
                                height: "22px",
                                cursor: params?.data?.report ? "pointer" : "",
                                filter: params?.data?.report ? "" : "grayscale(100%)",
                            }}
                            onClick={() => {
                                if (params?.data?.report) {
                                    handleClickOpenShareDialog(params.data);
                                }
                            }}
                        />
                    </Tooltip>
                    <Tooltip
                        title="Delete"
                        placement="top"
                    >
                        <img
                            src={deleteIcon}
                            alt="Delete"
                            style={{
                                width: "22px",
                                height: "22px",
                                cursor: params?.data?.report && params?.data?.created_by === userId ? "pointer" : "",
                                filter: params?.data?.report && params?.data?.created_by === userId ? "" : "grayscale(100%)",
                            }}
                            onClick={() => {
                                if (params?.data?.report && params?.data?.created_by === userId) {
                                    handleDeleteReport(params.data);
                                }
                            }}
                        />
                    </Tooltip>
                </div>
            ),
            headerComponentParams: {
                template: GetIconHeader("Action", action),
            },
        },
        {
            headerName: "Download",
            field: "download",
            maxWidth: 110,

            cellRenderer: (params) => (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Tooltip
                        title={params?.data?.report ? "Download" : "Processing"}
                        placement="top"
                    >
                        <img
                            src={downloadIcon}
                            alt="Download Icon"
                            style={{
                                width: "23px",
                                height: "17px",
                                cursor: params?.data?.report ? "pointer" : "",
                                filter: params?.data?.report ? "" : "grayscale(100%)",
                            }}
                            onClick={() => {
                                if (params?.data?.report) {
                                    handleDownloadReport(params);
                                }
                            }}
                        />
                    </Tooltip>
                </div>
            ),
            headerComponentParams: {
                template: GetIconHeader("Download", downloadIcon),
            },
        },
    ];

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            suppressMenu: true,
            sortable: true,
            filter: true,
            resizable: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
            },
            cellStyle: {
                fontSize: "14px",
                border: "none",
                padding: 10,
            },
            minWidth: 200,
        };
    }, []);

    return (
        <>
            {isLoading && <Loader />}
            <div>
                <div>
                    <div
                        className="ag-theme-alpine"
                        style={{
                            height: "calc(100vh - 320px)",
                            width: "100%",
                            marginLeft: "20px",
                        }}
                    >
                        <style>
                            {`
                                .ag-header-group-cell-label {
                                    justify-content: center !important;
                                }
                                .ag-header-cell-comp-wrapper {
                                    justify-content: center !important;
                                }
                            `}
                        </style>
                        <AgGridReact
                            columnDefs={columnDefs}
                            rowData={rowData}
                            defaultColDef={defaultColDef}
                            rowHeight={30}
                            headerHeight={60}
                        ></AgGridReact>
                    </div>
                    <Pagination
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        handlePageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50]}
                        totalCount={totalCount}
                        handleNextPageClick={handleNextPageClick}
                        handlePreviousPageClick={handlePreviousPageClick}
                        disableNextPage={!nextUrl}
                        disablePreviousPage={!previousUrl}
                    />
                    <div className="d-flex">
                        <span
                            style={{
                                fontSize: 12,
                                color: "#495057",
                                fontWeight: 600,
                                marginLeft: "auto",
                            }}
                        >
                            Total {rowData.length} rows
                        </span>
                    </div>
                </div>
            </div>
            {role && role.toLowerCase() === "admin" && (
                <AutoDeckReportShare
                    data={selectedReportForShare}
                    open={openShareDialog}
                    handleCloseShareDialog={handleCloseShareDialog}
                    fetchTableResult={getReports}
                />
            )}
        </>
    );
}

export default ReportGenerationView;
